import React, { useState, useEffect, useRef, useMemo } from 'react';
import FloatingNote  from './FloatingNote';
import FloatingCall  from './FloatingCall';
import FloatingEmail from './FloatingEmail';
import FloatingTask    from './FloatingTask';
import FloatingMeeting from './FloatingMeeting';
import FloatingVoiceMemo from './FloatingVoiceMemo';
import { loadBranches, saveBranches } from '../data/leads';
import RegionalSuggestionModal from './RegionalSuggestionModal';
import { activityService, latestActivityTimestampMs } from '../services/activityService';
import { loadCustomFields } from '../services/customFieldsService';
import LossReasonModal from './LossReasonModal';
import LeadCRMExtensions from './LeadCRMExtensions';
import { summarizeLeadWithAi } from '../services/aiFeatures';
import { useAiAction } from '../hooks/useAiAction';
import { AiSparkButton, AiErrorBanner, AiThinkingBar, AiNoKeyHint } from './AiAssist';
import { buildWhatsAppUrl } from '../utils/whatsappLink';
import QuoteModal from './QuoteModal';

const AVATAR_COLORS = [
  ['#159c5f','rgba(29,190,116,0.12)'],
  ['#22c55e','rgba(34,197,94,0.12)'],
  ['#f59e0b','rgba(245,158,11,0.12)'],
  ['#ab9bd6','rgba(171,155,214,0.12)'],
  ['#fb7185','rgba(251,113,133,0.12)'],
];

function Avatar({ name, id, size = 52 }) {
  const [color] = AVATAR_COLORS[id % AVATAR_COLORS.length];
  const initials = name.split(' ').map(x => x[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: `radial-gradient(circle at 32% 32%, ${color}55, ${color}18)`, color, border: `1.5px solid ${color}50`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.3, fontWeight: 700, flexShrink: 0, letterSpacing: '0.02em' }}>{initials}</div>
  );
}

/* ── Domain field: shows as clickable link, editable ── */
function DomainField({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal]         = useState(value || '');
  const [hover, setHover]     = useState(false);

  useEffect(() => { setVal(value || ''); }, [value]);

  function save() { const v = val.trim(); onChange(v); setEditing(false); }

  const href = val ? (val.startsWith('http') ? val : 'https://' + val) : null;
  const display = val ? val.replace(/^https?:\/\/(www\.)?/, '') : null;

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>Domain / Website</div>
      {editing ? (
        <div style={{ display: 'flex', gap: 5 }}>
          <input
            autoFocus value={val}
            onChange={e => setVal(e.target.value)}
            onBlur={save}
            onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setVal(value || ''); setEditing(false); } }}
            placeholder="z.B. example.de"
            style={{ flex: 1, height: 32, borderRadius: 6, border: '1.5px solid var(--accent)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, padding: '0 8px', outline: 'none', fontFamily: 'var(--mono)', boxSizing: 'border-box' }}
          />
          <button onClick={save} style={{ height: 32, padding: '0 10px', borderRadius: 6, background: 'var(--accent)', border: 'none', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>OK</button>
        </div>
      ) : (
        <div
          onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
          style={{ padding: '3px 6px', margin: '0 -6px', borderRadius: 5, background: hover ? 'var(--bg4)' : 'transparent', display: 'flex', alignItems: 'center', gap: 6, transition: 'background .1s', minHeight: 22 }}
        >
          {display ? (
            <a href={href} target="_blank" rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              style={{ flex: 1, fontSize: 13, color: 'var(--accent)', fontFamily: 'var(--mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5 }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ flexShrink: 0 }}><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
              {display}
            </a>
          ) : (
            <span style={{ flex: 1, fontSize: 13, color: 'var(--text3)' }}>–</span>
          )}
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2" onClick={() => setEditing(true)}
            style={{ flexShrink: 0, opacity: hover ? 0.65 : 0.18, transition: 'opacity .1s', cursor: 'text' }}>
            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
            <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
          </svg>
        </div>
      )}
    </div>
  );
}

/* ── Inline editable field ── */
function EditField({ label, value, onChange, mono, large, accent, type, options, placeholder }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal]         = useState(value || '');
  const [hover, setHover]     = useState(false);

  useEffect(() => { setVal(value || ''); }, [value]);

  function save() { const v = val.trim(); onChange(v); setEditing(false); }

  const textStyle = {
    fontSize: large ? 20 : 13,
    fontWeight: large ? 700 : 400,
    color: accent ? 'var(--accent)' : (value ? 'var(--text)' : 'var(--text3)'),
    fontFamily: mono ? 'var(--mono)' : 'inherit',
  };

  return (
    <div style={{ marginBottom: label ? 10 : 0 }}>
      {label && <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>{label}</div>}
      {editing ? (
        options ? (
          <select autoFocus value={val}
            onChange={e => { setVal(e.target.value); onChange(e.target.value); setEditing(false); }}
            onBlur={() => setEditing(false)}
            style={{ fontSize: large ? 17 : 13, width: '100%', border: '1.5px solid var(--accent)', borderRadius: 6, padding: '5px 8px', background: 'var(--bg2)', color: 'var(--text)', outline: 'none', fontFamily: 'var(--font)' }}>
            {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ) : (
          <input autoFocus type={type || 'text'} value={val}
            onChange={e => setVal(e.target.value)}
            onBlur={save}
            onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setVal(value || ''); setEditing(false); } }}
            style={{ fontSize: large ? 20 : 13, fontWeight: large ? 700 : 400, width: '100%', border: '1.5px solid var(--accent)', borderRadius: 6, padding: '5px 8px', background: 'var(--bg2)', color: 'var(--text)', outline: 'none', fontFamily: mono ? 'var(--mono)' : 'var(--font)', boxSizing: 'border-box' }}
          />
        )
      ) : (
        <div onClick={() => setEditing(true)}
          onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
          style={{ cursor: 'text', padding: '3px 6px', margin: '0 -6px', borderRadius: 5, background: hover ? 'var(--bg4)' : 'transparent', display: 'flex', alignItems: 'center', gap: 4, transition: 'background .1s', minHeight: large ? 28 : 22 }}>
          <span style={{ flex: 1, ...textStyle }}>{value || <span style={{ color: 'var(--text3)', fontWeight: 400, fontSize: 13 }}>{placeholder || '–'}</span>}</span>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2"
            style={{ flexShrink: 0, opacity: hover ? 0.65 : 0.18, transition: 'opacity .1s' }}>
            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
            <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
          </svg>
        </div>
      )}
    </div>
  );
}

/* ── Activity persistence via service ── */
function loadActivities(leadId) {
  const acts = activityService.getForLead(leadId);
  return acts.length > 0 ? acts : null;
}

function persistActivities(leadId, acts) {
  activityService.saveForLead(leadId, acts);
}

function getActivities(lead) {
  const saved = loadActivities(lead.id);
  if (saved) return saved;
  return [];
}

const ACTIVITY_ICON = {
  call:         { color: '#159c5f', bg: 'rgba(29,190,116,0.1)',  icon: <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/> },
  meeting:      { color: '#8f7cc9', bg: 'rgba(143,124,201,0.1)', icon: <><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></> },
  task:         { color: '#16a34a', bg: 'rgba(22,163,74,0.1)',  icon: <><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></> },
  note:         { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', icon: <><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/></> },
  segment:      { color: '#66796c', bg: 'rgba(102,121,108,0.1)', icon: <><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></> },
  email:        { color: '#3186ba', bg: 'rgba(49,134,186,0.1)', icon: <><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 7l10 7 10-7"/></> },
  wiedervorlage:{ color: '#f97316', bg: 'rgba(249,115,22,0.1)',  icon: <><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><path d="M8 14h.01M12 14h.01"/><path d="M16 14l-2 2 1 1 3-3"/></> },
  voice:        { color: '#ef4444', bg: 'rgba(239,68,68,0.1)',  icon: <><path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/><path d="M19 10v2a7 7 0 01-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></> },
};

/* ── Smart Status Suggestion ── */
function calcStatusSuggestion(lead, activities) {
  const status = (lead.status || '').toLowerCase().replace(/[\s-]+/g, '_');
  const offerStatus = lead.offerStatus || 'keins';
  let daysSinceLast = Infinity;
  const lastMs = latestActivityTimestampMs(activities);
  if (lastMs != null) {
    daysSinceLast = (Date.now() - lastMs) / 86400000;
  } else if (lead.activity) {
    const p = lead.activity.split('.');
    if (p.length === 3) {
      const d = new Date(+p[2], +p[1]-1, +p[0]);
      daysSinceLast = (Date.now() - d.getTime()) / 86400000;
    }
  }
  const hasMeeting  = activities.some(a => a.type === 'meeting');
  const totalActs   = activities.length;
  const deadStatuses = ['nicht_interessiert', 'verloren', 'abgesagt', 'kunde'];

  if (offerStatus === 'angenommen')
    return { text: 'Als Gewonnen markieren', sub: 'Angebot wurde angenommen', color: '#16a34a', dot: '#16a34a' };
  if (offerStatus === 'in_verhandlung' && daysSinceLast < 4)
    return { text: 'Deal jetzt abschließen', sub: 'Verhandlung läuft aktiv', color: '#f59e0b', dot: '#f59e0b' };
  if (['interessiert','nachgefasst','rückruf','callback'].includes(status) && offerStatus === 'keins' && totalActs >= 2)
    return { text: 'Angebot erstellen', sub: 'Leadqualität passt', color: '#159c5f', dot: '#159c5f' };
  if (['interessiert','nachgefasst'].includes(status) && !hasMeeting)
    return { text: 'Termin vereinbaren', sub: 'Noch kein Meeting geplant', color: '#8f7cc9', dot: '#8f7cc9' };
  if (daysSinceLast > 7 && status && !deadStatuses.includes(status))
    return { text: `Nachfassen`, sub: `${Math.round(daysSinceLast)} Tage ohne Aktivität`, color: '#f59e0b', dot: '#f59e0b' };
  if (['neu','erstkontakt'].includes(status) && totalActs === 0)
    return { text: 'Erstkontakt starten', sub: 'Noch kein Kontakt aufgenommen', color: '#159c5f', dot: '#159c5f' };
  return null;
}

/* ── Activity Heatmap (16 weeks) ── */
function ActivityHeatmap({ activities }) {
  const today = new Date(); today.setHours(0,0,0,0);
  const counts = {};
  activities.forEach(a => {
    let d;
    if (a.ts) d = new Date(a.ts);
    else if (a.date) {
      const p = a.date.split('.');
      d = p.length === 3 ? new Date(+p[2], +p[1]-1, +p[0]) : new Date(a.date);
    }
    if (!d || isNaN(d)) return;
    const key = d.toISOString().slice(0,10);
    counts[key] = (counts[key] || 0) + 1;
  });
  const days = [];
  for (let i = 111; i >= 0; i--) {
    const d = new Date(today); d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0,10);
    days.push({ key, count: counts[key] || 0, date: new Date(d) });
  }
  const maxCount = Math.max(...days.map(d => d.count), 1);
  const weeks = [];
  for (let w = 0; w < 16; w++) weeks.push(days.slice(w*7, (w+1)*7));
  const todayKey = today.toISOString().slice(0,10);
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>Aktivitäts-Heatmap</div>
      <div style={{ display: 'flex', gap: 3 }}>
        {weeks.map((week, wi) => (
          <div key={wi} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {week.map(day => {
              const intensity = day.count === 0 ? 0 : Math.max(0.18, day.count / maxCount);
              const bg = day.count === 0 ? 'rgba(102,121,108,0.12)' : `rgba(29,190,116,${intensity})`;
              return (
                <div key={day.key}
                  title={`${day.date.toLocaleDateString('de-DE', { day:'2-digit', month:'2-digit', year:'numeric' })}: ${day.count} Aktivität${day.count!==1?'en':''}`}
                  style={{ width: 12, height: 12, borderRadius: 3, background: bg,
                    border: day.key === todayKey ? '1.5px solid var(--accent)' : '1.5px solid transparent',
                    cursor: day.count > 0 ? 'default' : 'default', transition: 'transform .1s' }}
                />
              );
            })}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginTop: 8, justifyContent: 'flex-end' }}>
        <span style={{ fontSize: 9, color: 'var(--text3)' }}>Weniger</span>
        {[0, 0.18, 0.4, 0.65, 1].map((o, i) => (
          <div key={i} style={{ width: 10, height: 10, borderRadius: 2, background: o === 0 ? 'rgba(102,121,108,0.12)' : `rgba(29,190,116,${o})` }} />
        ))}
        <span style={{ fontSize: 9, color: 'var(--text3)' }}>Mehr</span>
      </div>
    </div>
  );
}

/* ── Deal Score calculation ── */
function calcDealScore(lead, activities) {
  const statusScores = {
    'neu': 5, 'erstkontakt': 8, 'versucht': 12, 'interessiert': 28,
    'rückruf': 32, 'callback': 32, 'nachgefasst': 38,
    'angebot': 50, 'terminiert': 58, 'termin': 58,
    'nicht_interessiert': 2, 'verloren': 2, 'abgesagt': 2, 'kunde': 100,
  };
  const status = (lead.status || '').toLowerCase().replace(/\s+/g, '_');
  let score = statusScores[status] ?? 15;
  if (score === 100) return 100;
  const actCount = activities.length;
  if (actCount >= 8) score += 12;
  else if (actCount >= 4) score += 7;
  else if (actCount >= 2) score += 3;
  const offerBonus = { 'versendet': 6, 'in_verhandlung': 16, 'angenommen': 25 };
  score += offerBonus[lead.offerStatus] ?? 0;
  if (lead.offerAmount && parseFloat(lead.offerAmount) > 0) score += 4;
  const lastMs = latestActivityTimestampMs(activities);
  if (lastMs != null) {
    const days = (Date.now() - lastMs) / 86400000;
    if (days < 2) score += 10;
    else if (days < 7) score += 6;
    else if (days < 14) score += 2;
  } else if (lead.activity) {
    const parts = lead.activity.split('.');
    if (parts.length === 3) {
      const d = new Date(+parts[2], +parts[1] - 1, +parts[0]);
      const days = (Date.now() - d.getTime()) / 86400000;
      if (days < 2) score += 10;
      else if (days < 7) score += 6;
      else if (days < 14) score += 2;
    }
  }
  return Math.min(95, Math.max(2, Math.round(score)));
}

/* ── Animated half-circle gauge ── */
function DealScoreGauge({ score }) {
  const [displayed, setDisplayed] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const start = Date.now();
    const duration = 900;
    const from = 0;
    function tick() {
      const elapsed = Date.now() - start;
      const t = Math.min(elapsed / duration, 1);
      const ease = 1 - Math.pow(1 - t, 3);
      setDisplayed(Math.round(from + (score - from) * ease));
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [score]);

  const r = 54, cx = 70, cy = 68, strokeW = 10;
  const circumference = Math.PI * r;
  const progress = displayed / 100;
  const offset = circumference * (1 - progress);

  const color = displayed >= 75 ? '#22c55e'
    : displayed >= 50 ? '#159c5f'
    : displayed >= 28 ? '#f59e0b'
    : '#ef4444';

  const label = displayed >= 80 ? 'Sehr hoch'
    : displayed >= 60 ? 'Hoch'
    : displayed >= 40 ? 'Mittel'
    : displayed >= 20 ? 'Niedrig'
    : 'Sehr niedrig';

  const pathD = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px 0 4px' }}>
      <svg width={140} height={82} style={{ overflow: 'visible' }}>
        <defs>
          <linearGradient id="gaugeGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ef4444" />
            <stop offset="40%" stopColor="#f59e0b" />
            <stop offset="70%" stopColor="#159c5f" />
            <stop offset="100%" stopColor="#22c55e" />
          </linearGradient>
          <linearGradient id="gaugeGlow" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ef4444" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#22c55e" stopOpacity="0.3" />
          </linearGradient>
        </defs>
        {/* Track */}
        <path d={pathD} fill="none" stroke="var(--bg4)" strokeWidth={strokeW} strokeLinecap="round" />
        {/* Glow */}
        <path d={pathD} fill="none" stroke="url(#gaugeGlow)"
          strokeWidth={strokeW + 8} strokeLinecap="round"
          strokeDasharray={`${circumference * progress} ${circumference}`}
          strokeDashoffset={0}
          opacity={0.5}
          style={{ filter: 'blur(4px)' }} />
        {/* Progress arc */}
        <path d={pathD} fill="none" stroke="url(#gaugeGrad)"
          strokeWidth={strokeW} strokeLinecap="round"
          strokeDasharray={`${circumference * progress} ${circumference}`}
          strokeDashoffset={0} />
        {/* Score text */}
        <text x={cx} y={cy - 4} textAnchor="middle" style={{ fontFamily: 'var(--font)', fontWeight: 800, fontSize: 28, fill: color }}>{displayed}%</text>
        <text x={cx} y={cy + 16} textAnchor="middle" style={{ fontFamily: 'var(--font)', fontWeight: 600, fontSize: 11, fill: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</text>
      </svg>
      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>Deal-Wahrscheinlichkeit</div>
    </div>
  );
}

const ACTION_BTNS = [
  { label: 'Notiz',   icon: <><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></> },
  { label: 'E-Mail',  icon: <><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 7l10 7 10-7"/></> },
  { label: 'Anruf',   icon: <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/> },
  { label: 'Aufgabe', icon: <><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></> },
  { label: 'Meeting', icon: <><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></> },
  { label: 'Voice',   icon: <><path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/><path d="M19 10v2a7 7 0 01-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></> },
];

const ACTIVITY_TABS = ['Alle', 'Notizen', 'E-Mails', 'Anrufe', 'Aufgaben', 'Meetings'];

const FALLBACK_SM = { label: '–', color: '#66796c', bg: 'rgba(102,121,108,0.1)' };

/* ── Branche dropdown with custom-add ── */
function BrancheSelect({ value, onChange }) {
  const [branches, setBranches] = useState(loadBranches);
  const [adding, setAdding]     = useState(false);
  const [draft, setDraft]       = useState('');
  const inputRef                = useRef(null);

  useEffect(() => { if (adding) inputRef.current?.focus(); }, [adding]);

  function handleSelect(e) {
    const v = e.target.value;
    if (v === '__add__') { setAdding(true); }
    else onChange(v);
  }

  function confirmAdd() {
    const trimmed = draft.trim();
    if (!trimmed) { setAdding(false); return; }
    const updated = [...branches, trimmed];
    saveBranches(updated);
    setBranches(updated);
    onChange(trimmed);
    setDraft('');
    setAdding(false);
  }

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>Branche</div>
      {adding ? (
        <div style={{ display: 'flex', gap: 5 }}>
          <input ref={inputRef} value={draft} onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') confirmAdd(); if (e.key === 'Escape') { setAdding(false); setDraft(''); } }}
            placeholder="z.B. Holzbau, Elektro…"
            style={{ flex: 1, height: 34, borderRadius: 7, border: '1.5px solid #159c5f', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, padding: '0 9px', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
          />
          <button onClick={confirmAdd}
            style={{ height: 34, padding: '0 12px', borderRadius: 7, background: '#159c5f', border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
            OK
          </button>
          <button onClick={() => { setAdding(false); setDraft(''); }}
            style={{ height: 34, width: 34, borderRadius: 7, background: 'var(--bg3)', border: '1px solid var(--border)', color: 'var(--text2)', fontSize: 14, cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            ✕
          </button>
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          <select value={value || ''} onChange={handleSelect}
            style={{ width: '100%', height: 36, borderRadius: 7, border: '1.5px solid #dee4de', padding: '0 28px 0 10px', fontSize: 13, color: value ? 'var(--text)' : 'var(--text3)', background: 'var(--bg2)', outline: 'none', fontFamily: 'inherit', cursor: 'pointer', appearance: 'none', boxSizing: 'border-box' }}
            onFocus={e => e.target.style.borderColor = '#159c5f'}
            onBlur={e => e.target.style.borderColor = '#dee4de'}>
            <option value="">– Branche wählen –</option>
            {branches.map(b => <option key={b} value={b}>{b}</option>)}
            <option disabled style={{ color: 'var(--border)' }}>──────────────</option>
            <option value="__add__">+ Eigene Branche hinzufügen…</option>
          </select>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#66796c" strokeWidth="2.5"
            style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </div>
      )}
    </div>
  );
}

/* ── Confetti burst ── */
function Confetti({ onDone }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width  = window.innerWidth;
    canvas.height = window.innerHeight;
    const W = canvas.width, H = canvas.height;

    const COLORS = ['#159c5f','#ef4444','#f59e0b','#22c55e','#ab9bd6','#fb7185','#3186ba','#f97316','#ec4899'];

    function mkParticle(x, y, angleMin, angleMax) {
      const angle = angleMin + Math.random() * (angleMax - angleMin);
      const speed = 10 + Math.random() * 14;
      return {
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        w: 7 + Math.random() * 7,
        h: 4 + Math.random() * 4,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        rot: Math.random() * Math.PI * 2,
        rotV: (Math.random() - 0.5) * 0.25,
        grav: 0.28 + Math.random() * 0.18,
        alpha: 1,
        round: Math.random() < 0.35,
      };
    }

    const particles = [];
    for (let i = 0; i < 90; i++) {
      // bottom-left corner → shoot up-right
      particles.push(mkParticle(0, H, -Math.PI * 0.85, -Math.PI * 0.35));
      // bottom-right corner → shoot up-left
      particles.push(mkParticle(W, H, -Math.PI * 0.65, -Math.PI * 0.15));
    }

    let frame = 0;
    let raf;
    function draw() {
      ctx.clearRect(0, 0, W, H);
      let alive = false;
      particles.forEach(p => {
        p.x  += p.vx;
        p.y  += p.vy;
        p.vy += p.grav;
        p.vx *= 0.992;
        p.rot += p.rotV;
        if (frame > 55) p.alpha -= 0.013;
        if (p.alpha <= 0) return;
        alive = true;
        ctx.save();
        ctx.globalAlpha = p.alpha;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        if (p.round) {
          ctx.beginPath();
          ctx.arc(0, 0, p.w / 2, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        }
        ctx.restore();
      });
      frame++;
      if (alive || frame < 60) raf = requestAnimationFrame(draw);
      else onDone?.();
    }
    draw();
    return () => cancelAnimationFrame(raf);
  }, []); // eslint-disable-line
  return <canvas ref={canvasRef} style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 9999 }} />;
}

function initLead(lead) {
  if (!lead) return lead;
  const d = { ...lead };
  if (!d.firstName && !d.lastName && d.name) {
    const parts = d.name.trim().split(/\s+/);
    d.firstName = parts[0] || '';
    d.lastName  = parts.slice(1).join(' ') || '';
  }
  // Split "Musterstraße 12a" into street + streetNo if not already split
  if (d.street && !d.streetNo) {
    const m = d.street.match(/^(.+?)\s+(\d+\s*\w*)$/);
    if (m) { d.street = m[1].trim(); d.streetNo = m[2].trim(); }
  }
  return d;
}

export default function LeadPanel({ lead, onClose, onUpdateLead, onDeleteLead, onSaveTask, statusMeta = {}, lists = [], bestandskunden = [], onActivitySaved, auditLog = [], leads = [], onRegionalSession, currentUser, authUsers = [], onLeadHandoff, userScopeId, onCommentMentions }) {
  const [actTab, setActTab]       = useState('Alle');
  const [expanded, setExpanded]   = useState(new Set([1]));
  const [showNote, setShowNote]   = useState(false);
  const [showCall, setShowCall]   = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [showTask,    setShowTask]    = useState(false);
  const [showMeeting, setShowMeeting] = useState(false);
  const [showVoice,   setShowVoice]   = useState(false);
  const [regionalSuggestionLead, setRegionalSuggestionLead] = useState(null);
  const [editingActivity, setEditingActivity] = useState(null); // activity being edited in FloatingCall
  const [inlineNoteId,   setInlineNoteId]   = useState(null);  // activity id with open inline note editor
  const [inlineNoteText, setInlineNoteText] = useState('');
  const [activities, setActivities] = useState([]);
  const [pinned, setPinned]       = useState(new Set());
  const [hoveredAct, setHoveredAct] = useState(null);
  const [editData, setEditData]   = useState(() => initLead(lead));
  const [confetti, setConfetti]   = useState(false);
  const [panelTab, setPanelTab]   = useState('Aktivitäten');
  const [showLossModal, setShowLossModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [deals, setDeals]         = useState([]);
  const [showDealForm, setShowDealForm] = useState(false);
  const [dealDraft, setDealDraft] = useState({ name: '', amount: '', stage: 'Qualifizierung' });
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);
  const [aiSummary, setAiSummary] = useState('');
  const [aiNextStep, setAiNextStep] = useState('');
  const aiLead = useAiAction();

  useEffect(() => {
    setActivities(getActivities(lead));
    setEditData(initLead(lead));
    setPinned(new Set());
    setExpanded(new Set([1]));
    setPanelTab('Aktivitäten');
    setDeals([]);
    setAttachments([]);
    setAiSummary('');
    setAiNextStep('');
  }, [lead?.id]);

  /* Nur Spiegel-Felder vom Parent (z. B. letzte Aktivität nach Timeline-Speichern), ohne Formular zurückzusetzen */
  useEffect(() => {
    if (!lead) return;
    setEditData(prev => {
      if (String(prev?.id ?? '') !== String(lead.id)) return prev;
      if (prev.activity === lead.activity) return prev;
      return { ...prev, activity: lead.activity };
    });
  }, [lead?.id, lead?.activity]);

  useEffect(() => {
    function reloadActs() {
      setActivities(getActivities(lead));
    }
    function onRemote(e) {
      const lid = e?.detail?.leadId;
      if (lid === '__all__' || (lid != null && String(lid) === String(lead?.id))) reloadActs();
    }
    function onStorage(ev) {
      if (ev.key === 'crm_activities') reloadActs();
    }
    window.addEventListener('crm-activities-changed', onRemote);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('crm-activities-changed', onRemote);
      window.removeEventListener('storage', onStorage);
    };
  }, [lead?.id]);

  if (!lead) return null;

  const sm = statusMeta[editData.status || lead.status] || FALLBACK_SM;
  const statusOptions = Object.entries(statusMeta).map(([k, v]) => ({ value: k, label: v.label }));

  const assignableLists = lists.filter(l => l.id !== 'all' && l.id !== 'top10');
  const listOptions = assignableLists.map(l => ({ value: l.id, label: l.label }));
  const currentListLabel = lists.find(l => l.id === editData.list)?.label || editData.list?.toUpperCase() || '–';

  function updateField(field, value) {
    const updated = { ...editData, [field]: value };
    setEditData(updated);
    onUpdateLead?.(lead.id, { [field]: value });
    if (field === 'status') {
      const label = (statusMeta[value]?.label || '').toLowerCase();
      if (label.includes('verkauft')) setConfetti(true);
    }
  }

  function updateNameField(field, value) {
    const firstName = field === 'firstName' ? value : (editData.firstName || '');
    const lastName  = field === 'lastName'  ? value : (editData.lastName  || '');
    const name = [firstName, lastName].filter(Boolean).join(' ') || editData.name || '';
    const changes = { [field]: value, name };
    setEditData(prev => ({ ...prev, ...changes }));
    onUpdateLead?.(lead.id, changes);
  }

  function handleSaveNote(note) {
    const exists = activities.some(a => a.id === note.id);
    let next;
    if (exists) {
      next = activities.map(a => a.id === note.id ? { ...a, ...note } : a);
    } else {
      next = [note, ...activities];
      setExpanded(prev => new Set([note.id, ...prev]));
      if (onActivitySaved) setTimeout(onActivitySaved, 500);
    }
    setActivities(next);
    persistActivities(lead.id, next);
    setShowNote(false);
    setEditingActivity(null);
  }

  function handleSaveCall(call) {
    const exists = activities.some(a => a.id === call.id);
    let next;
    if (exists) {
      next = activities.map(a => a.id === call.id ? { ...a, ...call } : a);
      setEditingActivity(null);
    } else {
      next = [call, ...activities];
      setExpanded(prev => new Set([call.id, ...prev]));
      if (onActivitySaved) {
        setTimeout(() => onActivitySaved(call), 500);
      } else if (call.outcome === 'reached_appt') {
        setRegionalSuggestionLead(editData);
      }
    }
    setActivities(next);
    persistActivities(lead.id, next);
    setShowCall(false);
    if (!exists && call.outcome === 'reached_appt') {
      window.dispatchEvent(new CustomEvent('crm-appointment-set', { detail: { outcome: 'reached_appt', leadId: lead.id } }));
    }
  }

  function saveInlineNote(actId) {
    const next = activities.map(a => a.id === actId ? { ...a, note: inlineNoteText.trim() || null } : a);
    setActivities(next);
    persistActivities(lead.id, next);
    setInlineNoteId(null);
    setInlineNoteText('');
  }

  function deleteActivity(id) {
    const next = activities.filter(a => a.id !== id);
    setActivities(next);
    persistActivities(lead.id, next);
    setPinned(prev => { const n = new Set(prev); n.delete(id); return n; });
  }

  function togglePin(id) {
    setPinned(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  function toggleExpand(id) {
    setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  const filtered = activities.filter(a => {
    if (actTab === 'Alle')     return true;
    if (actTab === 'Notizen')  return a.type === 'note';
    if (actTab === 'Anrufe')   return a.type === 'call';
    if (actTab === 'Aufgaben') return a.type === 'task';
    if (actTab === 'Meetings') return a.type === 'meeting';
    if (actTab === 'E-Mails')  return a.type === 'email';
    return true;
  });

  const byMonth = [];
  filtered.forEach(a => {
    const last = byMonth[byMonth.length - 1];
    if (!last || last.month !== a.month) byMonth.push({ month: a.month, items: [a] });
    else last.items.push(a);
  });

  const pinnedActivities = activities.filter(a => pinned.has(a.id));

  return (
    <div className="gl-leadpanel" style={{ display: 'flex', width: '100%', height: '100%', overflow: 'hidden' }}>

      {/* ══════════════ LEFT COLUMN ══════════════ */}
      <div style={{ width: 300, minWidth: 300, background: 'transparent', borderRight: '1px solid var(--glass-border)', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>

        {/* Back / Close */}
        <div style={{ padding: '12px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <button onClick={onClose} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--accent)', fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><polyline points="15 18 9 12 15 6"/></svg>
            Kontakte
          </button>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {/* Lock toggle */}
            {onUpdateLead && (() => {
              const isAdmin = !currentUser?.role || currentUser.role === 'admin' || currentUser.role === 'manager';
              const isOwner = !lead.ownerId || lead.ownerId === currentUser?.id;
              const canToggleLock = isAdmin || isOwner;
              return canToggleLock ? (
                <button
                  onClick={() => onUpdateLead(lead.id, { locked: !lead.locked, lockedBy: !lead.locked ? currentUser?.id : null })}
                  title={lead.locked ? 'Lead entsperren' : 'Lead sperren (nur Besitzer/Admin kann bearbeiten)'}
                  style={{ width: 28, height: 28, borderRadius: 8, background: lead.locked ? 'rgba(245,158,11,0.1)' : 'var(--bg4)', border: lead.locked ? '1px solid rgba(245,158,11,0.3)' : '1px solid transparent', color: lead.locked ? '#d97706' : 'var(--text3)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .12s' }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{lead.locked ? <><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></> : <><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 019.9-1"/></>}</svg>
                </button>
              ) : null;
            })()}
            {onDeleteLead && (
              <button onClick={() => { if (window.confirm(`"${lead.name}" in den Papierkorb verschieben?`)) onDeleteLead(lead.id); }}
                title="In Papierkorb"
                style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(220,38,38,0.07)', border: '1px solid rgba(220,38,38,0.2)', color: '#dc2626', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
              </button>
            )}
            <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--bg4)', border: 'none', color: 'var(--text2)', fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
          </div>
        </div>

        {/* ── Hero Header ── */}
        <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 16 }}>
            {/* Avatar with gradient ring */}
            <div style={{ padding: 3, borderRadius: '50%', flexShrink: 0, background: `linear-gradient(135deg, ${AVATAR_COLORS[lead.id % AVATAR_COLORS.length][0]}, rgba(255,255,255,0.55), ${AVATAR_COLORS[(lead.id + 2) % AVATAR_COLORS.length][0]})` }}>
              <Avatar name={editData.name || lead.name} id={lead.id} size={62} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', gap: 5, marginBottom: 2 }}>
                <EditField large value={editData.firstName || ''} onChange={v => updateNameField('firstName', v)} placeholder="Vorname" />
                <EditField large value={editData.lastName  || ''} onChange={v => updateNameField('lastName',  v)} placeholder="Nachname" />
              </div>
              <EditField value={editData.company} onChange={v => updateField('company', v)} placeholder="Unternehmen" accent />
            </div>
          </div>

          {/* ── Action Tray ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 18 }}>
            {/* Row 1 — Primary actions: wide horizontal pills */}
            <div style={{ display: 'flex', gap: 7 }}>
              {[
                { label: 'Anruf',  color: '#16a34a', bg: 'rgba(22,163,74,0.09)',  onClick: () => setShowCall(true),  icon: <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/> },
                { label: 'E-Mail', color: '#3186ba', bg: 'rgba(49,134,186,0.09)', onClick: () => setShowEmail(true), icon: <><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 7l10 7 10-7"/></> },
              ].map(({ label, color, bg, onClick, icon }) => (
                <button key={label} onClick={onClick} style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
                  padding: '11px 16px', borderRadius: 12,
                  background: bg, border: `1px solid ${color}35`,
                  backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
                  color, cursor: 'pointer',
                  boxShadow: `inset 0 1px 0 rgba(255,255,255,0.70), 0 1px 4px rgba(0,0,0,0.04)`,
                  transition: 'transform .10s var(--motion-ease), box-shadow .12s ease',
                }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = `0 8px 20px ${color}28, inset 0 1px 0 rgba(255,255,255,0.80)`; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = `inset 0 1px 0 rgba(255,255,255,0.70), 0 1px 4px rgba(0,0,0,0.04)`; }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">{icon}</svg>
                  <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '-0.01em' }}>{label}</span>
                </button>
              ))}
            </div>
            {/* Row 2 — Secondary actions: 3 compact pills */}
            <div style={{ display: 'flex', gap: 7 }}>
              {[
                { label: 'Notiz',   color: '#d97706', bg: 'rgba(217,119,6,0.08)',   onClick: () => setShowNote(true),    icon: <><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></> },
                { label: 'Aufgabe', color: '#7a5fb8', bg: 'rgba(122,95,184,0.08)',  onClick: () => setShowTask(true),    icon: <><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></> },
                { label: 'Meeting', color: '#db2777', bg: 'rgba(219,39,119,0.08)',  onClick: () => setShowMeeting(true), icon: <><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></> },
              ].map(({ label, color, bg, onClick, icon }) => (
                <button key={label} onClick={onClick} style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                  padding: '8px 10px', borderRadius: 10,
                  background: bg, border: `1px solid ${color}28`,
                  backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                  color, cursor: 'pointer',
                  boxShadow: `inset 0 1px 0 rgba(255,255,255,0.60)`,
                  transition: 'transform .10s var(--motion-ease), box-shadow .12s ease',
                }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = `0 6px 16px ${color}24, inset 0 1px 0 rgba(255,255,255,0.75)`; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = `inset 0 1px 0 rgba(255,255,255,0.60)`; }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">{icon}</svg>
                  <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '-0.005em' }}>{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Kontaktstatus – prominent blue card */}
          <div style={{ border: '1.5px solid rgba(29,190,116,0.35)', borderRadius: 10, background: 'rgba(29,190,116,0.04)', padding: '8px 12px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#159c5f', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Kontaktstatus</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: sm.color, flexShrink: 0, boxShadow: `0 0 0 2px ${sm.color}33` }} />
              <select value={editData.status || ''} onChange={e => updateField('status', e.target.value)}
                style={{ flex: 1, fontSize: 13, fontWeight: 600, color: sm.color, border: 'none', background: 'transparent', outline: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
                {statusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#159c5f" strokeWidth="2.5" style={{ flexShrink: 0, pointerEvents: 'none' }}><polyline points="6 9 12 15 18 9"/></svg>
            </div>
          </div>

          {/* Smart Status Suggestion */}
          {(() => {
            const sug = calcStatusSuggestion(editData, activities);
            if (!sug) return null;
            return (
              <div style={{ marginTop: 10, padding: '8px 11px', borderRadius: 9, background: sug.color + '0f',
                border: `1px solid ${sug.color}28`, display: 'flex', alignItems: 'center', gap: 9,
                animation: 'glSuggestionIn .25s var(--motion-ease) both' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={sug.color} strokeWidth="2" style={{ flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: sug.color, lineHeight: 1.2 }}>{sug.text}</div>
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1, lineHeight: 1.2 }}>{sug.sub}</div>
                </div>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: sug.dot,
                  animation: 'glDotPulse 2s ease-in-out infinite', flexShrink: 0 }} />
              </div>
            );
          })()}

          {/* KI Lead-Zusammenfassung */}
          <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 9, background: 'var(--bg3)', border: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>KI-Zusammenfassung</span>
              <AiSparkButton
                small
                label="Erstellen"
                loading={aiLead.loading}
                onClick={async () => {
                  const snippets = activities.slice(0, 15).map(a => {
                    const parts = [a.title, a.note, a.outcome].filter(Boolean);
                    return parts.join(' · ');
                  }).filter(Boolean);
                  if (editData.note) snippets.unshift(`Lead-Notiz: ${editData.note}`);
                  const sm = statusMeta[editData.status];
                  const result = await aiLead.run(() => summarizeLeadWithAi({
                    leadName: editData.name || lead.name,
                    company: editData.company,
                    status: sm?.label || editData.status,
                    snippets,
                  }));
                  if (result) {
                    setAiSummary(result.summary || '');
                    setAiNextStep(result.suggestedNextStep || '');
                  }
                }}
              />
            </div>
            {!aiLead.configured && <AiNoKeyHint compact />}
            {aiLead.loading && <AiThinkingBar label="KI fasst Lead zusammen…" />}
            {aiSummary && (
              <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.6, marginBottom: aiNextStep ? 8 : 0 }}>{aiSummary}</div>
            )}
            {aiNextStep && (
              <div style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 600, lineHeight: 1.45 }}>
                → {aiNextStep}
              </div>
            )}
            {!aiSummary && !aiLead.loading && aiLead.configured && (
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>Aus Aktivitäten & Notizen eine Kurzfassung erzeugen.</div>
            )}
            <AiErrorBanner message={aiLead.error} onDismiss={aiLead.clearError} />
          </div>
        </div>

        {/* Kontaktstatus (Liste & Score) */}
        <LeftSection title="Kontaktstatus">
          <div style={{ background: 'var(--bg)', border: '1px solid #e2ebe3', borderRadius: 8, padding: '10px 12px', marginBottom: 4 }}>
            {/* Liste dropdown */}
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>Liste</div>
              <div style={{ position: 'relative' }}>
                <select value={editData.list || ''} onChange={e => updateField('list', e.target.value)}
                  style={{ width: '100%', height: 36, borderRadius: 7, border: '1.5px solid #dee4de', padding: '0 30px 0 10px', fontSize: 13, color: 'var(--text)', background: 'var(--bg2)', outline: 'none', fontFamily: 'inherit', cursor: 'pointer', appearance: 'none' }}
                  onFocus={e => e.target.style.borderColor = '#159c5f'}
                  onBlur={e => e.target.style.borderColor = '#dee4de'}>
                  <option value="">– Keine Liste –</option>
                  {listOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#66796c" strokeWidth="2.5" style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}><polyline points="6 9 12 15 18 9"/></svg>
              </div>
            </div>
            <EditField label="Score" value={String(editData.score ?? '')} onChange={v => updateField('score', Number(v) || 0)} type="number" placeholder="0–100" />
          </div>
        </LeftSection>

        {/* Kontaktdetails */}
        <LeftSection title="Kontaktdetails">
          <div style={{ background: 'var(--bg)', border: '1px solid #e2ebe3', borderRadius: 8, padding: '10px 12px', marginBottom: 4 }}>
            <EditField label="E-Mail" value={editData.email} onChange={v => updateField('email', v)} type="email" accent placeholder="E-Mail hinzufügen" />
            <EditField label="Telefon" value={editData.tel} onChange={v => updateField('tel', v)} mono placeholder="Telefon hinzufügen" />
            <DomainField value={editData.domain} onChange={v => updateField('domain', v)} />
            <div style={{ height: 1, background: 'var(--border)', margin: '6px 0' }} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px', gap: 8 }}>
              <EditField label="Straße" value={editData.street || ''} onChange={v => updateField('street', v)} placeholder="Musterstraße" />
              <EditField label="Nr." value={editData.streetNo || ''} onChange={v => updateField('streetNo', v)} placeholder="12" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: 8 }}>
              <EditField label="PLZ" value={editData.zip || ''} onChange={v => updateField('zip', v)} mono placeholder="12345" />
              <EditField label="Stadt" value={editData.city || ''} onChange={v => updateField('city', v)} placeholder="Stadt hinzufügen" />
            </div>
            <EditField label="Land" value={editData.country || ''} onChange={v => updateField('country', v)} placeholder="Deutschland" />
            <BrancheSelect value={editData.branch} onChange={v => updateField('branch', v)} />
            <EditField label="Offene Stellen" value={editData.jobs > 0 ? String(editData.jobs) : ''} onChange={v => updateField('jobs', Number(v) || 0)} type="number" placeholder="0" />
            <div style={{ marginBottom: 4 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>Letzte Aktivität</div>
              <div style={{ fontSize: 13, color: 'var(--text)', paddingLeft: 6 }}>{editData.activity || '–'}</div>
            </div>
            {loadCustomFields().map(f => (
              <EditField key={f.id} label={f.label}
                value={editData[f.id] !== undefined ? String(editData[f.id]) : ''}
                onChange={v => updateField(f.id, f.type === 'number' ? (Number(v) || 0) : v)}
                type={f.type === 'number' ? 'number' : f.type === 'url' ? 'url' : 'text'}
                placeholder={f.label + ' hinzufügen'} />
            ))}
          </div>
        </LeftSection>

        <LeadCRMExtensions
          editData={editData}
          lead={lead}
          leads={leads}
          activities={activities}
          currentUser={currentUser}
          authUsers={authUsers}
          onUpdateField={updateField}
          onHandoff={onLeadHandoff}
          userScopeId={userScopeId}
          onCommentMentions={onCommentMentions}
        />
      </div>

      {/* ══════════════ MIDDLE COLUMN ══════════════ */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'transparent' }}>

        {/* Top panel tabs */}
        <div className="gl-topnav" style={{ display: 'flex', flexShrink: 0, paddingLeft: 4, zIndex: 2 }}>
          {['Aktivitäten', 'Timeline', 'Überblick', 'Intelligence', 'Wiedervorlage', 'Verlauf'].map(t => (
            <button key={t} onClick={() => setPanelTab(t)} style={{ padding: '13px 16px', fontSize: 13, fontWeight: t === panelTab ? 600 : 400, color: t === panelTab ? 'var(--text)' : 'var(--text2)', borderBottom: t === panelTab ? '2px solid var(--accent)' : '2px solid transparent', background: 'none', border: 'none', cursor: 'pointer', transition: 'color .14s var(--motion-ease), border-color .14s var(--motion-ease)', whiteSpace: 'nowrap' }}>{t}</button>
          ))}
        </div>

        {/* ── Action buttons ── */}
        <div style={{ padding: '10px 20px', borderBottom: '1px solid var(--glass-border)', background: 'rgba(255,255,255,0.3)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
          {ACTION_BTNS.map(btn => (
            <button key={btn.label}
              onClick={() => {
                if (btn.label === 'Notiz')    setShowNote(true);
                if (btn.label === 'E-Mail')   setShowEmail(true);
                if (btn.label === 'Anruf')  {
                  const tel = (editData.tel || lead.tel || '').replace(/\s+/g, '');
                  if (tel) window.location.href = `tel:${tel}`;
                  setShowCall(true);
                }
                if (btn.label === 'Aufgabe')  setShowTask(true);
                if (btn.label === 'Meeting')  setShowMeeting(true);
                if (btn.label === 'Voice')    setShowVoice(true);
              }}
              style={{ height: 38, padding: '0 18px', borderRadius: 8, background: 'var(--bg3)', border: '1.5px solid var(--border)', color: 'var(--text2)', fontSize: 13, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, transition: 'all .12s', whiteSpace: 'nowrap' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--accent-bg)'; e.currentTarget.style.color = 'var(--accent)'; e.currentTarget.style.borderColor = 'var(--accent-border)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg3)'; e.currentTarget.style.color = 'var(--text2)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">{btn.icon}</svg>
              {btn.label}
            </button>
          ))}
          {(() => {
            const waUrl = buildWhatsAppUrl(editData.tel !== '–' ? editData : lead);
            if (!waUrl) return null;
            return (
              <button
                type="button"
                onClick={() => window.open(waUrl, '_blank', 'noopener,noreferrer')}
                style={{ height: 38, padding: '0 18px', borderRadius: 8, background: 'var(--bg3)', border: '1.5px solid var(--border)', color: '#25d366', fontSize: 13, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, transition: 'all .12s', whiteSpace: 'nowrap' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(37,211,102,0.08)'; e.currentTarget.style.borderColor = 'rgba(37,211,102,0.4)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg3)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" strokeLinecap="round" strokeLinejoin="round"/></svg>
                WhatsApp
              </button>
            );
          })()}
          {/* Angebot erstellen */}
          <button
            type="button"
            onClick={() => setShowQuoteModal(true)}
            style={{ height: 38, padding: '0 18px', borderRadius: 8, background: 'var(--bg3)', border: '1.5px solid var(--border)', color: '#0f8a53', fontSize: 13, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, transition: 'all .12s', whiteSpace: 'nowrap' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(15,138,83,0.08)'; e.currentTarget.style.borderColor = 'rgba(15,138,83,0.3)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg3)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/></svg>
            Angebot
          </button>
        </div>

        {/* Activity sub-tabs — only visible in Aktivitäten tab */}
        {panelTab === 'Aktivitäten' && (
          <div style={{ background: 'var(--bg2)', borderBottom: '1px solid var(--border)', padding: '0 20px', display: 'flex', gap: 2, flexShrink: 0 }}>
            {ACTIVITY_TABS.map(t => (
              <button key={t} onClick={() => setActTab(t)} style={{ padding: '9px 13px', fontSize: 13, fontWeight: actTab === t ? 600 : 400, color: actTab === t ? 'var(--accent)' : 'var(--text2)', borderBottom: actTab === t ? '2px solid var(--accent)' : '2px solid transparent', background: 'none', border: 'none', cursor: 'pointer', transition: 'color .12s' }}>{t}</button>
            ))}
          </div>
        )}

        {/* ── Aktivitäten Timeline ── */}
        {panelTab === 'Aktivitäten' && (
          <div style={{ flex: 1, overflowY: 'auto', padding: '22px 26px 40px', background: 'var(--bg3)' }}>
            {pinnedActivities.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="var(--accent)" stroke="var(--accent)" strokeWidth="1.5"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                  Gepinnt ({pinnedActivities.length})
                </div>
                {pinnedActivities.map(act => {
                  const ai = ACTIVITY_ICON[act.type] || ACTIVITY_ICON.note;
                  return (
                    <div key={act.id} style={{ background: 'var(--accent-bg)', borderRadius: 10, border: '1px solid var(--accent-border)', padding: '10px 14px', marginBottom: 8, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                      <div style={{ width: 28, height: 28, borderRadius: 8, background: ai.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={ai.color} strokeWidth="1.8">{ai.icon}</svg>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{act.title}</div>
                        {act.note && <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{act.note}</div>}
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>{act.date} · {act.by}</div>
                      </div>
                      <button onClick={() => togglePin(act.id)} title="Entpinnen" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', flexShrink: 0, padding: 2 }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    </div>
                  );
                })}
                <div style={{ height: 1, background: 'var(--border)', margin: '16px 0 4px' }} />
              </div>
            )}

            {byMonth.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, paddingTop: 60, color: 'var(--text3)' }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" style={{ opacity: 0.4 }}>
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
                </svg>
                <div style={{ fontSize: 14, fontWeight: 500 }}>Noch keine Aktivitäten</div>
                <div style={{ fontSize: 12 }}>Anruf, Notiz oder Meeting hinzufügen</div>
              </div>
            ) : byMonth.map(group => (
              <div key={group.month} style={{ marginBottom: 8 }}>
                {/* Month divider */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                  <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.10em', whiteSpace: 'nowrap' }}>{group.month}</span>
                  <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                </div>
                {/* Timeline rail */}
                <div style={{ position: 'relative', paddingLeft: 24 }}>
                  <div style={{ position: 'absolute', left: 8, top: 14, bottom: 14, width: 2, background: 'linear-gradient(to bottom, var(--accent), transparent)', opacity: 0.22, borderRadius: 1, pointerEvents: 'none' }} />
                {group.items.map(act => {
                  const ai   = ACTIVITY_ICON[act.type] || ACTIVITY_ICON.note;
                  const open = expanded.has(act.id);
                  const isPinned = pinned.has(act.id);
                  return (
                    <div key={act.id} style={{ position: 'relative', marginBottom: 10 }}>
                    {/* Timeline dot */}
                    <div style={{ position: 'absolute', left: -20, top: 14, width: 10, height: 10, borderRadius: '50%', background: ai.color, border: '2px solid var(--bg3)', boxShadow: `0 0 7px ${ai.color}66`, zIndex: 1, pointerEvents: 'none' }} />
                    <div
                      onMouseEnter={() => setHoveredAct(act.id)}
                      onMouseLeave={() => setHoveredAct(null)}
                      className="gl-card"
                      style={{ borderRadius: 12, overflow: 'hidden', position: 'relative', transition: 'box-shadow .18s var(--motion-ease), transform .16s var(--motion-spring)', willChange: 'transform' }}>
                      {hoveredAct === act.id && (
                        <div style={{ position: 'absolute', top: 8, right: 10, display: 'flex', gap: 4, zIndex: 2 }}>
                          <button onClick={e => { e.stopPropagation(); togglePin(act.id); }} title={isPinned ? 'Entpinnen' : 'Anpinnen'}
                            style={{ width: 28, height: 28, borderRadius: 6, background: isPinned ? 'var(--accent-bg)' : 'var(--bg3)', border: `1.5px solid ${isPinned ? 'var(--accent-border)' : 'var(--border)'}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: isPinned ? 'var(--accent)' : 'var(--text3)', transition: 'all .1s' }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill={isPinned ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                          </button>
                          {/* Edit button: full edit for calls, inline note otherwise */}
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              if (act.type === 'call') {
                                setEditingActivity(act);
                              } else {
                                setInlineNoteId(act.id);
                                setInlineNoteText(act.note || '');
                                setExpanded(prev => new Set([...prev, act.id]));
                              }
                            }}
                            title="Bearbeiten"
                            style={{ width: 28, height: 28, borderRadius: 6, background: 'var(--bg3)', border: '1.5px solid var(--border)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text3)', transition: 'all .1s' }}
                            onMouseEnter={e => { e.currentTarget.style.color = 'var(--accent)'; e.currentTarget.style.background = 'var(--accent-bg)'; e.currentTarget.style.borderColor = 'var(--accent-border)'; }}
                            onMouseLeave={e => { e.currentTarget.style.color = 'var(--text3)'; e.currentTarget.style.background = 'var(--bg3)'; e.currentTarget.style.borderColor = 'var(--border)'; }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                          <button onClick={e => { e.stopPropagation(); deleteActivity(act.id); }} title="Löschen"
                            style={{ width: 28, height: 28, borderRadius: 6, background: 'var(--bg3)', border: '1.5px solid var(--border)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text3)', transition: 'all .1s' }}
                            onMouseEnter={e => { e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.background = 'rgba(220,38,38,0.08)'; e.currentTarget.style.borderColor = 'rgba(220,38,38,0.3)'; }}
                            onMouseLeave={e => { e.currentTarget.style.color = 'var(--text3)'; e.currentTarget.style.background = 'var(--bg3)'; e.currentTarget.style.borderColor = 'var(--border)'; }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/></svg>
                          </button>
                        </div>
                      )}
                      <button onClick={() => toggleExpand(act.id)} style={{ width: '100%', padding: '13px 16px', display: 'flex', alignItems: 'flex-start', gap: 12, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                        <div style={{ width: 34, height: 34, borderRadius: 10, background: ai.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={ai.color} strokeWidth="1.8">{ai.icon}</svg>
                        </div>
                        <div style={{ flex: 1, minWidth: 0, paddingRight: hoveredAct === act.id ? 100 : 20 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{act.title}</span>
                            {act.done && <span style={{ fontSize: 11, color: '#16a34a', background: 'rgba(22,163,74,0.1)', border: '1px solid rgba(22,163,74,0.2)', padding: '1px 8px', borderRadius: 20 }}>✓ Erledigt</span>}
                            {isPinned && <svg width="11" height="11" viewBox="0 0 24 24" fill="var(--accent)" stroke="var(--accent)" strokeWidth="1.5"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 3 }}>von {act.by} · {act.date} um {act.time} Uhr{act.duration ? ` · ${act.duration} Min.` : ''}</div>
                          {!open && act.note && (
                            <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{act.note}</div>
                          )}
                        </div>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2" style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s', marginTop: 9 }}>
                          <polyline points="6 9 12 15 18 9"/>
                        </svg>
                      </button>
                      {open && (
                        <div style={{ borderTop: '1px solid var(--border)', padding: '12px 16px 14px 62px' }}>
                          {/* Outcome badge for calls */}
                          {act.type === 'call' && act.outcome && (() => {
                            const OUTCOME_COLORS = {
                              reached_appt:    { color: '#16a34a', bg: 'rgba(22,163,74,0.1)',   border: 'rgba(22,163,74,0.25)' },
                              reached_followup:{ color: '#0f8a53', bg: 'rgba(15,138,83,0.08)',  border: 'rgba(15,138,83,0.2)' },
                              reached_no:      { color: '#dc2626', bg: 'rgba(220,38,38,0.08)',  border: 'rgba(220,38,38,0.2)' },
                              not_voicemail:   { color: '#d97706', bg: 'rgba(217,119,6,0.08)',  border: 'rgba(217,119,6,0.2)' },
                              not_busy:        { color: '#7a5fb8', bg: 'rgba(122,95,184,0.08)', border: 'rgba(122,95,184,0.2)' },
                              not_noanswer:    { color: '#6c7a70', bg: 'rgba(107,114,128,0.08)',border: 'rgba(107,114,128,0.2)' },
                              gatekeeper:      { color: '#92400e', bg: 'rgba(146,64,14,0.08)',  border: 'rgba(146,64,14,0.2)' },
                              decision_maker:  { color: '#0f766e', bg: 'rgba(15,118,110,0.1)', border: 'rgba(15,118,110,0.22)' },
                              callback:        { color: '#0891b2', bg: 'rgba(8,145,178,0.08)',  border: 'rgba(8,145,178,0.2)' },
                            };
                            const oc = OUTCOME_COLORS[act.outcome] || { color: 'var(--text3)', bg: 'var(--bg4)', border: 'var(--border)' };
                            return (
                              <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 20, color: oc.color, background: oc.bg, border: `1px solid ${oc.border}`, display: 'inline-block', marginBottom: 8 }}>
                                {act.outcomeLabel || act.outcome}
                              </span>
                            );
                          })()}

                          {/* Voice memo playback */}
                          {act.type === 'voice' && act.audioData && (
                            <div style={{ marginBottom: 10 }}>
                              <audio controls src={act.audioData}
                                style={{ width: '100%', height: 34, borderRadius: 8, outline: 'none' }} />
                            </div>
                          )}

                          {/* Inline note editor */}
                          {inlineNoteId === act.id ? (
                            <div>
                              <textarea
                                autoFocus
                                value={inlineNoteText}
                                onChange={e => setInlineNoteText(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Escape') { setInlineNoteId(null); setInlineNoteText(''); } }}
                                placeholder="Notiz eingeben…"
                                rows={3}
                                style={{ width: '100%', borderRadius: 7, border: '1.5px solid var(--accent)', background: 'var(--bg3)', color: 'var(--text)', fontSize: 13, padding: '8px 10px', outline: 'none', resize: 'vertical', lineHeight: 1.65, fontFamily: 'inherit', boxSizing: 'border-box' }}
                              />
                              <div style={{ display: 'flex', gap: 6, marginTop: 7 }}>
                                <button
                                  onClick={() => saveInlineNote(act.id)}
                                  style={{ height: 28, padding: '0 14px', borderRadius: 6, background: 'var(--accent)', border: 'none', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                  Speichern
                                </button>
                                <button
                                  onClick={() => { setInlineNoteId(null); setInlineNoteText(''); }}
                                  style={{ height: 28, padding: '0 12px', borderRadius: 6, background: 'var(--bg4)', border: '1px solid var(--border)', color: 'var(--text2)', fontSize: 12, cursor: 'pointer' }}>
                                  Abbrechen
                                </button>
                              </div>
                            </div>
                          ) : act.note ? (
                            <div>
                              <div style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.7, marginBottom: 6, whiteSpace: 'pre-wrap' }}>{act.note}</div>
                              {act.type !== 'segment' && (
                                <button
                                  onClick={() => { setInlineNoteId(act.id); setInlineNoteText(act.note || ''); }}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--text3)', padding: 0, display: 'flex', alignItems: 'center', gap: 4, transition: 'color .1s' }}
                                  onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
                                  onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}>
                                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                  Notiz bearbeiten
                                </button>
                              )}
                            </div>
                          ) : (
                            act.type !== 'segment' && (
                              <button
                                onClick={() => { setInlineNoteId(act.id); setInlineNoteText(''); }}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--text3)', padding: 0, display: 'flex', alignItems: 'center', gap: 4, transition: 'color .1s' }}
                                onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
                                onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                                Notiz hinzufügen
                              </button>
                            )
                          )}
                        </div>
                      )}
                    </div>
                    </div>
                  );
                })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Timeline Tab ── */}
        {panelTab === 'Timeline' && (
          <div style={{ flex: 1, overflowY: 'auto', padding: '22px 26px 40px' }}>
            {activities.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'var(--text3)', paddingTop: 40, fontSize: 13 }}>Noch keine Aktivitäten protokolliert.</div>
            ) : (() => {
              const sorted = [...activities].sort((a, b) => new Date(b.ts || b.date || 0) - new Date(a.ts || a.date || 0));
              const groups = [];
              sorted.forEach(a => {
                const d = a.ts ? new Date(a.ts) : (a.date ? new Date(a.date) : null);
                const monthKey = d ? d.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' }) : 'Ohne Datum';
                const last = groups[groups.length - 1];
                if (!last || last.month !== monthKey) groups.push({ month: monthKey, items: [a] });
                else last.items.push(a);
              });
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                  {groups.map(({ month, items }) => (
                    <div key={month}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12, paddingBottom: 6, borderBottom: '1px solid var(--border)' }}>{month}</div>
                      <div style={{ position: 'relative' }}>
                        <div style={{ position: 'absolute', left: 13, top: 0, bottom: 0, width: 2, background: 'var(--border)', borderRadius: 1 }} />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {items.map(a => {
                            const ai = ACTIVITY_ICON[a.type] || ACTIVITY_ICON.note;
                            const d = a.ts ? new Date(a.ts) : null;
                            const timeStr = d ? d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '';
                            const dateStr = d ? d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) : '';
                            const outcomeStr = a.outcomeLabel || a.outcome || '';
                            return (
                              <div key={a.id} style={{ display: 'flex', gap: 10, paddingLeft: 2 }}>
                                <div style={{ width: 24, height: 24, borderRadius: '50%', background: ai.bg, border: `2px solid var(--bg)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, zIndex: 1, position: 'relative', marginLeft: 1 }}>
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={ai.color} strokeWidth="2">{ai.icon}</svg>
                                </div>
                                <div style={{ flex: 1, background: 'var(--bg3)', border: `1px solid var(--border)`, borderLeft: `3px solid ${ai.color}`, borderRadius: '0 8px 8px 0', padding: '8px 12px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                                    <div>
                                      <span style={{ fontSize: 12, fontWeight: 700, color: ai.color }}>{a.title || a.type}</span>
                                      {outcomeStr && <span style={{ fontSize: 10, marginLeft: 6, background: ai.bg, color: ai.color, borderRadius: 4, padding: '1px 5px', fontWeight: 600 }}>{outcomeStr}</span>}
                                    </div>
                                    <span style={{ fontSize: 10, color: 'var(--text3)', whiteSpace: 'nowrap', flexShrink: 0 }}>{dateStr}{timeStr ? ` · ${timeStr}` : ''}</span>
                                  </div>
                                  {(a.note || a.text) && <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 5, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{a.note || a.text}</div>}
                                  <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                                    {a.duration && <span style={{ fontSize: 10, color: 'var(--text3)' }}>⏱ {a.duration}</span>}
                                    {a.by && <span style={{ fontSize: 10, color: 'var(--text3)' }}>von {a.by}</span>}
                                    {a.callQuality > 0 && <span style={{ fontSize: 10, color: '#f59e0b' }}>{'★'.repeat(a.callQuality)}</span>}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}

        {/* ── Überblick Tab ── */}
        {panelTab === 'Überblick' && (
          <div style={{ flex: 1, overflowY: 'auto', padding: '22px 26px 40px', background: 'transparent' }}>

            {/* Deal Score Gauge */}
            <div className="gl-card" style={{ borderRadius: 16, padding: '16px 18px 14px', marginBottom: 18, textAlign: 'center' }}>
              <DealScoreGauge score={calcDealScore(lead, activities)} key={lead.id + activities.length} />
            </div>

            {/* Activity stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 18 }}>
              {[
                { label: 'Anrufe',   count: activities.filter(a => a.type === 'call').length,    color: '#159c5f' },
                { label: 'Meetings', count: activities.filter(a => a.type === 'meeting').length, color: '#8f7cc9' },
                { label: 'E-Mails',  count: activities.filter(a => a.type === 'email').length,   color: '#3186ba' },
                { label: 'Notizen',  count: activities.filter(a => a.type === 'note').length,    color: '#f59e0b' },
                { label: 'Aufgaben', count: activities.filter(a => a.type === 'task').length,    color: '#16a34a' },
                { label: 'Gesamt',   count: activities.length,                                   color: '#66796c' },
              ].map(s => (
                <div key={s.label} className="gl-card" style={{ borderRadius: 10, padding: '12px', textAlign: 'center', animation: 'glFadeUp .2s var(--motion-ease) both' }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.count}</div>
                  <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 700, marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Activity Heatmap */}
            <div className="gl-card" style={{ borderRadius: 14, padding: '16px 18px', marginBottom: 18, animation: 'glFadeUp .28s var(--motion-ease) both' }}>
              <ActivityHeatmap activities={activities} />
            </div>

            {/* Contact info card */}
            <div style={{ background: 'var(--bg2)', borderRadius: 12, border: '1px solid var(--border)', padding: '16px 18px', marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 12 }}>Kontaktinformationen</div>
              {[
                { label: 'E-Mail',          value: editData.email,    color: 'var(--accent)' },
                { label: 'Telefon',         value: editData.tel },
                { label: 'Stadt',           value: editData.city },
                { label: 'Branche',         value: editData.branch },
                { label: 'Score',           value: editData.score !== undefined ? String(editData.score) : null },
                { label: 'Offene Stellen',  value: editData.jobs > 0 ? `${editData.jobs} Stelle${editData.jobs > 1 ? 'n' : ''}` : null },
                { label: 'Liste',           value: currentListLabel },
                { label: 'Letzte Aktivität',value: editData.activity },
              ].filter(r => r.value).map((r, i, arr) => (
                <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 13, marginBottom: i < arr.length - 1 ? 8 : 0, paddingBottom: i < arr.length - 1 ? 8 : 0, borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <span style={{ color: 'var(--text3)', fontWeight: 500, flexShrink: 0, marginRight: 12 }}>{r.label}</span>
                  <span style={{ color: r.color || 'var(--text)', fontWeight: 500, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>{r.value}</span>
                </div>
              ))}
            </div>

            {/* Angebotsstatus card */}
            {(() => {
              const OFFER_OPTS = [
                { key: 'keins',          label: 'Kein Angebot',   color: '#66796c' },
                { key: 'versendet',      label: 'Versendet',      color: '#159c5f' },
                { key: 'in_verhandlung', label: 'In Verhandlung', color: '#f59e0b' },
                { key: 'angenommen',     label: 'Angenommen',     color: '#16a34a' },
                { key: 'abgelehnt',      label: 'Abgelehnt',      color: '#dc2626' },
              ];
              const currentStatus = editData.offerStatus || 'keins';
              const statusMeta = OFFER_OPTS.find(o => o.key === currentStatus) || OFFER_OPTS[0];
              return (
                <div style={{ background: 'var(--bg2)', borderRadius: 12, border: `1px solid ${currentStatus !== 'keins' ? statusMeta.color + '44' : 'var(--border)'}`, padding: '16px 18px', marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Angebot</div>
                    {currentStatus !== 'keins' && editData.offerAmount > 0 && (
                      <div style={{ fontSize: 14, fontWeight: 700, color: statusMeta.color }}>
                        {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(editData.offerAmount)}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: currentStatus !== 'keins' ? 12 : 0 }}>
                    {OFFER_OPTS.map(opt => {
                      const isActive = currentStatus === opt.key;
                      return (
                        <button key={opt.key}
                          onClick={() => {
                            onUpdateLead?.(lead.id, { offerStatus: opt.key }, ['offerStatus']);
                            setEditData(prev => ({ ...prev, offerStatus: opt.key }));
                            if (opt.key === 'abgelehnt') setShowLossModal(true);
                          }}
                          style={{ padding: '4px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600, border: `1.5px solid ${isActive ? opt.color : 'var(--border)'}`, background: isActive ? opt.color + '18' : 'var(--bg3)', color: isActive ? opt.color : 'var(--text3)', cursor: 'pointer', transition: 'all .12s' }}>
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  {currentStatus !== 'keins' && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                      <div>
                        <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600, marginBottom: 5 }}>Betrag (€)</div>
                        <input type="number" min="0" step="100" placeholder="0"
                          value={editData.offerAmount || ''}
                          onChange={e => { const v = e.target.value ? Number(e.target.value) : null; onUpdateLead?.(lead.id, { offerAmount: v }, ['offerAmount']); setEditData(prev => ({ ...prev, offerAmount: v })); }}
                          style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--bg3)', color: 'var(--text)', fontSize: 14, padding: '0 10px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600, marginBottom: 5 }}>Angebotsdatum</div>
                        <input type="date"
                          value={editData.offerDate || ''}
                          onChange={e => { onUpdateLead?.(lead.id, { offerDate: e.target.value || null }, ['offerDate']); setEditData(prev => ({ ...prev, offerDate: e.target.value || null })); }}
                          style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--bg3)', color: 'var(--text)', fontSize: 13, padding: '0 8px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600, marginBottom: 5 }}>Erw. Abschluss</div>
                        <input type="date"
                          value={editData.closeDate || ''}
                          onChange={e => { onUpdateLead?.(lead.id, { closeDate: e.target.value || null }, ['closeDate']); setEditData(prev => ({ ...prev, closeDate: e.target.value || null })); }}
                          style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--bg3)', color: 'var(--text)', fontSize: 13, padding: '0 8px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Recent activities compact */}
            {activities.slice(0, 3).length > 0 && (
              <div style={{ background: 'var(--bg2)', borderRadius: 12, border: '1px solid var(--border)', padding: '16px 18px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 12 }}>Letzte Aktivitäten</div>
                {activities.slice(0, 3).map((act, i) => {
                  const ai = ACTIVITY_ICON[act.type] || ACTIVITY_ICON.note;
                  return (
                    <div key={act.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', paddingBottom: i < 2 ? 10 : 0, marginBottom: i < 2 ? 10 : 0, borderBottom: i < 2 ? '1px solid var(--border)' : 'none' }}>
                      <div style={{ width: 28, height: 28, borderRadius: 7, background: ai.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={ai.color} strokeWidth="1.8">{ai.icon}</svg>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{act.title}</div>
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{act.date} · {act.by}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Wiedervorlage Tab ── */}
        {panelTab === 'Wiedervorlage' && (
          <div style={{ flex: 1, overflowY: 'auto', padding: '22px 26px 40px' }}>
            <div style={{ background: 'var(--bg2)', borderRadius: 12, border: '1px solid var(--border)', padding: '18px 20px', marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 12 }}>Schnellauswahl</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
                {[
                  { label: 'Morgen',    days: 1 },
                  { label: '+3 Tage',   days: 3 },
                  { label: '+1 Woche',  days: 7 },
                  { label: '+2 Wochen', days: 14 },
                  { label: '+1 Monat',  days: 30 },
                ].map(opt => {
                  const d = new Date(); d.setDate(d.getDate() + opt.days);
                  const v = d.toISOString().slice(0, 10);
                  const isActive = editData.wiedervorlage === v;
                  return (
                    <button key={opt.label} onClick={() => {
                      onUpdateLead?.(lead.id, { wiedervorlage: v }, ['wiedervorlage']);
                      setEditData(prev => ({ ...prev, wiedervorlage: v }));
                    }} style={{ height: 30, padding: '0 12px', borderRadius: 20, fontSize: 12, fontWeight: isActive ? 700 : 500, cursor: 'pointer', border: `1.5px solid ${isActive ? '#ab9bd6' : 'var(--border)'}`, background: isActive ? 'rgba(171,155,214,0.12)' : 'var(--bg3)', color: isActive ? '#ab9bd6' : 'var(--text2)', fontFamily: 'inherit', transition: 'all .12s' }}>
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Oder konkretes Datum</div>
              <input
                type="date"
                value={editData.wiedervorlage || ''}
                min={new Date().toISOString().slice(0, 10)}
                onChange={e => {
                  const v = e.target.value;
                  onUpdateLead?.(lead.id, { wiedervorlage: v || null }, ['wiedervorlage']);
                  setEditData(prev => ({ ...prev, wiedervorlage: v || null }));
                }}
                style={{ height: 40, borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--bg3)', color: 'var(--text)', fontSize: 14, padding: '0 12px', outline: 'none', width: '100%', boxSizing: 'border-box' }}
              />
              {editData.wiedervorlage && (
                <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(171,155,214,0.08)', borderRadius: 8, padding: '8px 12px' }}>
                  <div style={{ fontSize: 13, color: '#ab9bd6', fontWeight: 600 }}>
                    {new Date(editData.wiedervorlage).toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
                  </div>
                  <button onClick={() => { onUpdateLead?.(lead.id, { wiedervorlage: null }); setEditData(prev => ({ ...prev, wiedervorlage: null })); }}
                    style={{ fontSize: 12, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>✕ Entfernen</button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Verlauf / Timeline Tab ── */}
        {panelTab === 'Verlauf' && (
          <div style={{ flex: 1, overflowY: 'auto', padding: '22px 26px 40px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 16 }}>Aktivitäts-Timeline</div>
            {(() => {
              const FIELD_LABELS = { status: 'Status', offerStatus: 'Angebotsstatus', offerAmount: 'Angebotsbetrag', offerDate: 'Angebotsdatum', lossReason: 'Verlustgrund', wiedervorlage: 'Wiedervorlage', assignedTo: 'Zugewiesen an', closeDate: 'Erw. Abschluss' };
              const actItems = activities.map(a => ({ id: a.id, ts: a.ts || a.date || '', kind: 'activity', type: a.type, title: a.title, note: a.note, duration: a.duration, outcome: a.outcome, by: a.by }));
              const auditItems = auditLog.map(e => ({ id: e.id, ts: e.timestamp, kind: 'audit', field: e.field, oldValue: e.oldValue, newValue: e.newValue, by: e.userName }));
              const all = [...actItems, ...auditItems].sort((a, b) => new Date(b.ts) - new Date(a.ts));
              if (!all.length) return <div style={{ textAlign: 'center', color: 'var(--text3)', paddingTop: 40, fontSize: 13 }}>Noch keine Aktivitäten protokolliert.</div>;
              return (
                <div style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', left: 15, top: 0, bottom: 0, width: 2, background: 'var(--border)', borderRadius: 1 }} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {all.map(item => {
                      const ai = item.kind === 'activity' ? (ACTIVITY_ICON[item.type] || ACTIVITY_ICON.note) : { color: '#66796c', bg: 'rgba(102,121,108,0.1)', icon: <><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></> };
                      const tsDate = item.ts ? new Date(item.ts) : null;
                      const tsStr = tsDate ? tsDate.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
                      return (
                        <div key={item.id} style={{ display: 'flex', gap: 12, paddingLeft: 4 }}>
                          <div style={{ width: 26, height: 26, borderRadius: '50%', background: ai.bg, border: `2px solid var(--bg)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, zIndex: 1, position: 'relative', marginLeft: 1 }}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={ai.color} strokeWidth="2">{ai.icon}</svg>
                          </div>
                          <div style={{ flex: 1, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', marginTop: 0 }}>
                            {item.kind === 'activity' ? (
                              <>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{item.title}</span>
                                  <span style={{ fontSize: 10, color: 'var(--text3)', flexShrink: 0 }}>{tsStr}</span>
                                </div>
                                {item.note && <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4, lineHeight: 1.5 }}>{item.note}</div>}
                                <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                                  {item.outcome && <span style={{ fontSize: 10, color: ai.color, background: ai.bg, borderRadius: 4, padding: '1px 6px', fontWeight: 600 }}>{item.outcome}</span>}
                                  {item.duration && <span style={{ fontSize: 10, color: 'var(--text3)' }}>{item.duration}</span>}
                                  {item.by && <span style={{ fontSize: 10, color: 'var(--text3)' }}>von {item.by}</span>}
                                </div>
                              </>
                            ) : (
                              <>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{FIELD_LABELS[item.field] || item.field} geändert</span>
                                  <span style={{ fontSize: 10, color: 'var(--text3)', flexShrink: 0 }}>{tsStr}</span>
                                </div>
                                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 4 }}>
                                  <span style={{ background: 'rgba(220,38,38,0.08)', color: '#dc2626', borderRadius: 4, padding: '1px 6px', fontSize: 11, fontFamily: 'var(--mono)' }}>{String(item.oldValue ?? '–')}</span>
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                                  <span style={{ background: 'rgba(34,197,94,0.08)', color: '#16a34a', borderRadius: 4, padding: '1px 6px', fontSize: 11, fontFamily: 'var(--mono)' }}>{String(item.newValue ?? '–')}</span>
                                  {item.by && <span style={{ fontSize: 10, color: 'var(--text3)', marginLeft: 4 }}>von {item.by}</span>}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* ── Intelligence Tab ── */}
        {panelTab === 'Intelligence' && (
          <div style={{ flex: 1, overflowY: 'auto', padding: '22px 26px 40px', background: 'var(--bg3)' }}>
            {/* Score Analysis */}
            <div style={{ background: 'var(--bg2)', borderRadius: 12, border: '1px solid var(--border)', padding: '16px 18px', marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 14 }}>Score-Analyse</div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontSize: 13, color: 'var(--text)' }}>Lead-Score</span>
                <span style={{ fontSize: 22, fontWeight: 700, color: (editData.score ?? 0) >= 70 ? '#16a34a' : (editData.score ?? 0) >= 40 ? '#f59e0b' : '#ef4444', lineHeight: 1 }}>{editData.score ?? 0}</span>
              </div>
              <div style={{ height: 10, background: 'var(--bg4)', borderRadius: 5, overflow: 'hidden', marginBottom: 10 }}>
                <div style={{ height: '100%', width: `${editData.score ?? 0}%`, borderRadius: 5, background: (editData.score ?? 0) >= 70 ? 'linear-gradient(90deg,#16a34a,#22c55e)' : (editData.score ?? 0) >= 40 ? 'linear-gradient(90deg,#d97706,#f59e0b)' : 'linear-gradient(90deg,#dc2626,#ef4444)', transition: 'width .6s ease' }} />
              </div>
              <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.5 }}>
                {(editData.score ?? 0) >= 70 ? 'Hohe Priorität – sofort kontaktieren und Termin sichern.' : (editData.score ?? 0) >= 40 ? 'Mittlere Priorität – Follow-Up einplanen und Interesse wecken.' : 'Niedrige Priorität – in Liste halten und bei Bedarf erneut ansprechen.'}
              </div>
            </div>

            {/* Empfohlene nächste Aktion */}
            <div style={{ background: 'var(--bg2)', borderRadius: 12, border: '1px solid var(--border)', padding: '16px 18px', marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 12 }}>Empfohlene nächste Aktion</div>
              {(() => {
                const status = editData.status;
                const actions = {
                  hot:      { color: '#ef4444', bg: 'rgba(239,68,68,0.08)',  icon: <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>, title: 'Sofort anrufen', desc: 'Heißer Lead – jetzt den Termin sichern bevor das Interesse abkühlt.' },
                  warm:     { color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', icon: <><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 014-4h14M7 23l-4-4 4-4"/><path d="M21 13v2a4 4 0 01-4 4H3"/></>, title: 'Follow-Up einplanen', desc: 'Warm gehaltener Kontakt – mit einem personalisierten Follow-Up reaktivieren.' },
                  cold:     { color: '#66796c', bg: 'rgba(102,121,108,0.08)', icon: <><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 7l10 7 10-7"/></>, title: 'E-Mail senden', desc: 'Kalter Kontakt – mit einer passenden Vorlage das Interesse neu entfachen.' },
                  follow:   { color: '#ab9bd6', bg: 'rgba(171,155,214,0.08)', icon: <><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></>, title: 'Aufgabe erstellen', desc: 'Follow-Up ausstehend – eine konkrete Aufgabe mit Fälligkeitsdatum anlegen.' },
                  new:      { color: '#22c55e', bg: 'rgba(34,197,94,0.08)',  icon: <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>, title: 'Erstanruf tätigen', desc: 'Neuer Lead – so früh wie möglich den ersten Kontakt herstellen.' },
                  verkauft: { color: '#16a34a', bg: 'rgba(22,163,74,0.08)',  icon: <><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/></>, title: 'Erfolg dokumentieren', desc: 'Abschluss erzielt – Bestandskunde anlegen und Referenz für ähnliche Branchen nutzen.' },
                  blocked:  { color: '#5a5a60', bg: 'rgba(90,90,96,0.08)',   icon: <><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></>, title: 'In 60 Tagen reaktivieren', desc: 'Blockierter Kontakt – Pause einlegen und mit neuem Aufhänger wieder ansprechen.' },
                };
                const a = actions[status] || actions.new;
                return (
                  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px', borderRadius: 10, background: a.bg, border: `1.5px solid ${a.color}33` }}>
                    <div style={{ width: 36, height: 36, borderRadius: 9, background: a.color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={a.color} strokeWidth="1.8">{a.icon}</svg>
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: a.color, marginBottom: 4 }}>{a.title}</div>
                      <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.5 }}>{a.desc}</div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Jobs insight */}
            {editData.jobs > 0 && (
              <div style={{ background: 'var(--bg2)', borderRadius: 12, border: '1.5px solid rgba(22,163,74,0.25)', padding: '14px 18px', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#16a34a' }}>Recruiting-Signal</span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.5 }}>
                  <strong style={{ color: 'var(--text)' }}>{editData.company}</strong> hat aktuell <strong style={{ color: '#16a34a' }}>{editData.jobs} offene Stelle{editData.jobs > 1 ? 'n' : ''}</strong> ausgeschrieben – idealer Gesprächsaufhänger für Recruiting-Leistungen.
                </div>
              </div>
            )}

            {/* Bestandskunden matching */}
            <div style={{ background: 'var(--bg2)', borderRadius: 12, border: '1px solid var(--border)', padding: '16px 18px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>Branchenreferenzen</div>
              {!editData.branch ? (
                <div style={{ fontSize: 13, color: 'var(--text3)', textAlign: 'center', padding: '10px 0' }}>Branche beim Lead hinterlegen um passende Referenzkunden zu sehen.</div>
              ) : (
                <div style={{ fontSize: 13, color: 'var(--text2)' }}>Wechsle zur rechten Spalte für passende Bestandskunden in <strong style={{ color: 'var(--text)' }}>{editData.branch}</strong>.</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ══════════════ RIGHT COLUMN ══════════════ */}
      <div style={{ width: 300, minWidth: 300, background: 'var(--bg2)', borderLeft: '1px solid var(--border)', overflowY: 'auto' }}>

        {/* Unternehmen */}
        <RightSection title="Unternehmen" count={1} actionLabel="+ Hinzufügen">
          <div style={{ padding: '12px 14px', background: 'var(--bg3)', borderRadius: 10, border: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--accent-bg)', border: '1.5px solid var(--accent-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'var(--accent)', flexShrink: 0 }}>
                {(editData.company || lead.company).slice(0, 2).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{editData.company || lead.company}</div>
                {editData.city && <div style={{ fontSize: 12, color: 'var(--text3)' }}>{editData.city}</div>}
              </div>
              <span style={{ fontSize: 11, background: 'var(--accent)', color: 'white', borderRadius: 5, padding: '2px 7px', fontWeight: 600, flexShrink: 0 }}>Primär</span>
            </div>
            <RightDetailRow label="Telefon" value={editData.tel} />
            {editData.jobs > 0 && <RightDetailRow label="Offene Stellen" value={`${editData.jobs} Stelle${editData.jobs > 1 ? 'n' : ''}`} valueColor="#16a34a" />}
            <RightDetailRow label="Score" value={String(editData.score ?? '')} />
          </div>
        </RightSection>

        {/* Gepinnte Aktivitäten */}
        {pinnedActivities.length > 0 && (
          <RightSection title="Gepinnte Aktivitäten" count={pinnedActivities.length}>
            {pinnedActivities.map(act => {
              const ai = ACTIVITY_ICON[act.type] || ACTIVITY_ICON.note;
              return (
                <div key={act.id} style={{ padding: '10px 12px', background: 'var(--bg3)', borderRadius: 10, border: '1px solid var(--border)', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ width: 24, height: 24, borderRadius: 6, background: ai.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={ai.color} strokeWidth="2">{ai.icon}</svg>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{act.title}</div>
                      {act.note && <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{act.note}</div>}
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>{act.date}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </RightSection>
        )}

        {/* Kontaktsatz */}
        <RightSection title="Kontaktsatz">
          <div style={{ padding: '12px 14px', background: 'var(--bg3)', borderRadius: 10, border: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <Avatar name={editData.name || lead.name} id={lead.id} size={32} />
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{editData.name || lead.name}</span>
            </div>
            <RightDetailRow label="E-Mail" value={editData.email} link />
            <RightDetailRow label="Telefon" value={editData.tel} />
            <RightDetailRow label="Kontaktstatus" value={sm?.label} valueColor={sm?.color} />
            <RightDetailRow label="Liste" value={currentListLabel} />
            <RightDetailRow label="Stadt" value={editData.city} />
          </div>
        </RightSection>

        {/* Ähnliche Bestandskunden */}
        <BestandskundenSection branch={editData.branch} bestandskunden={bestandskunden} />

        {/* Deals */}
        <RightSection title="Deals" count={deals.length} actionLabel="+ Hinzufügen" onAction={() => setShowDealForm(true)}>
          {showDealForm && (
            <div style={{ background: 'var(--bg3)', borderRadius: 10, border: '1.5px solid var(--accent-border)', padding: '12px 14px', marginBottom: 10 }}>
              <input
                autoFocus
                placeholder="Deal-Name"
                value={dealDraft.name}
                onChange={e => setDealDraft(d => ({ ...d, name: e.target.value }))}
                style={{ width: '100%', height: 32, borderRadius: 6, border: '1.5px solid var(--border)', padding: '0 9px', fontSize: 13, background: 'var(--bg2)', color: 'var(--text)', outline: 'none', marginBottom: 7, boxSizing: 'border-box', fontFamily: 'inherit' }}
                onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'}
              />
              <input
                type="number"
                placeholder="Wert in €"
                value={dealDraft.amount}
                onChange={e => setDealDraft(d => ({ ...d, amount: e.target.value }))}
                style={{ width: '100%', height: 32, borderRadius: 6, border: '1.5px solid var(--border)', padding: '0 9px', fontSize: 13, background: 'var(--bg2)', color: 'var(--text)', outline: 'none', marginBottom: 7, boxSizing: 'border-box', fontFamily: 'inherit' }}
                onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'}
              />
              <select value={dealDraft.stage} onChange={e => setDealDraft(d => ({ ...d, stage: e.target.value }))}
                style={{ width: '100%', height: 32, borderRadius: 6, border: '1.5px solid var(--border)', padding: '0 9px', fontSize: 13, background: 'var(--bg2)', color: 'var(--text)', outline: 'none', marginBottom: 10, boxSizing: 'border-box', fontFamily: 'inherit', cursor: 'pointer' }}>
                {['Qualifizierung', 'Angebot', 'Verhandlung', 'Abschluss'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  onClick={() => {
                    if (!dealDraft.name.trim()) return;
                    setDeals(prev => [...prev, { id: Date.now(), ...dealDraft, date: new Date().toLocaleDateString('de-DE') }]);
                    setDealDraft({ name: '', amount: '', stage: 'Qualifizierung' });
                    setShowDealForm(false);
                  }}
                  style={{ flex: 1, height: 30, borderRadius: 6, background: 'var(--accent)', border: 'none', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  Erstellen
                </button>
                <button onClick={() => { setShowDealForm(false); setDealDraft({ name: '', amount: '', stage: 'Qualifizierung' }); }}
                  style={{ flex: 1, height: 30, borderRadius: 6, background: 'var(--bg4)', border: '1px solid var(--border)', color: 'var(--text2)', fontSize: 12, cursor: 'pointer' }}>
                  Abbrechen
                </button>
              </div>
            </div>
          )}
          {deals.length === 0 && !showDealForm ? (
            <div style={{ textAlign: 'center', padding: '20px 14px', color: 'var(--text3)', fontSize: 13 }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" style={{ display: 'block', margin: '0 auto 8px', opacity: 0.35 }}>
                <line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/>
              </svg>
              Keine Deals verknüpft
            </div>
          ) : deals.map(deal => (
            <div key={deal.id} style={{ padding: '10px 12px', background: 'var(--bg3)', borderRadius: 9, border: '1px solid var(--border)', marginBottom: 7, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 3 }}>{deal.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>{deal.stage} · {deal.date}</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                {deal.amount && <span style={{ fontSize: 13, fontWeight: 700, color: '#16a34a' }}>{Number(deal.amount).toLocaleString('de-DE')} €</span>}
                <button onClick={() => setDeals(prev => prev.filter(d => d.id !== deal.id))}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 0, fontSize: 11, transition: 'color .1s' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#dc2626'}
                  onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>
                </button>
              </div>
            </div>
          ))}
        </RightSection>

        {/* Aufgaben */}
        <RightSection title="Aufgaben" count={1}>
          <div style={{ padding: '10px 12px', background: 'var(--bg3)', borderRadius: 10, border: '1px solid var(--border)', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <div style={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid var(--accent)', flexShrink: 0, marginTop: 2 }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Follow-Up vorbereiten</div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 3 }}>Fällig: {editData.activity || lead.activity}</div>
            </div>
          </div>
        </RightSection>

        {/* Anhänge */}
        <RightSection title="Anhänge" count={attachments.length || undefined} actionLabel="Hinzufügen" onAction={() => fileInputRef.current?.click()}>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={e => {
              const files = Array.from(e.target.files).map(f => ({ id: Date.now() + Math.random(), name: f.name, size: f.size, date: new Date().toLocaleDateString('de-DE') }));
              setAttachments(prev => [...prev, ...files]);
              e.target.value = '';
            }}
          />
          {attachments.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 14px', color: 'var(--text3)', fontSize: 13 }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" style={{ display: 'block', margin: '0 auto 8px', opacity: 0.35 }}>
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
              </svg>
              Keine Anhänge
            </div>
          ) : attachments.map(att => (
            <div key={att.id} style={{ padding: '8px 11px', background: 'var(--bg3)', borderRadius: 8, border: '1px solid var(--border)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" style={{ flexShrink: 0 }}><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>{(att.size / 1024).toFixed(1)} KB · {att.date}</div>
              </div>
              <button onClick={() => setAttachments(prev => prev.filter(a => a.id !== att.id))}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', flexShrink: 0, padding: 2 }}
                onMouseEnter={e => e.currentTarget.style.color = '#dc2626'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
          ))}
        </RightSection>
      </div>

      {/* ── Floating windows ── */}
      {showNote  && <FloatingNote  lead={editData} currentUser={currentUser} onSave={handleSaveNote} onClose={() => setShowNote(false)} />}
      {showCall  && <FloatingCall  lead={editData} currentUser={currentUser} onSave={handleSaveCall} onSaveTask={task => { onSaveTask?.(task); }} onClose={() => setShowCall(false)} />}
      {editingActivity?.type === 'call' && (
        <FloatingCall lead={editData} activity={editingActivity} currentUser={currentUser} onSave={handleSaveCall} onSaveTask={task => { onSaveTask?.(task); }} onClose={() => setEditingActivity(null)} />
      )}
      {showEmail && <FloatingEmail lead={editData} onClose={() => setShowEmail(false)} />}
      {showQuoteModal && (
        <QuoteModal
          lead={editData}
          currentUser={currentUser}
          onClose={() => setShowQuoteModal(false)}
          onUpdateLead={onUpdateLead}
          company={currentUser?.company || ''}
        />
      )}
      {showTask    && <FloatingTask    lead={editData} currentUser={currentUser} users={authUsers} onSave={task => { onSaveTask?.(task); setShowTask(false); }} onClose={() => setShowTask(false)} />}
      {showMeeting && <FloatingMeeting lead={editData} currentUser={currentUser} onSave={m => { const next = [{ ...m, id: m.id, type: 'meeting' }, ...activities]; setActivities(next); persistActivities(lead.id, next); setShowMeeting(false); }} onClose={() => setShowMeeting(false)} />}
      {showVoice && (
        <FloatingVoiceMemo
          lead={editData}
          currentUser={currentUser}
          onSave={memo => {
            const next = [memo, ...activities];
            setActivities(next);
            persistActivities(lead.id, next);
            setShowVoice(false);
          }}
          onClose={() => setShowVoice(false)}
        />
      )}
      {regionalSuggestionLead && (
        <RegionalSuggestionModal
          sourceLead={regionalSuggestionLead}
          onClose={() => setRegionalSuggestionLead(null)}
          onStartSession={session => { onRegionalSession?.(session); setRegionalSuggestionLead(null); }}
        />
      )}
      {confetti && <Confetti onDone={() => setConfetti(false)} />}
      {showLossModal && (
        <LossReasonModal
          lead={editData}
          onConfirm={data => {
            onUpdateLead?.(lead.id, data, ['lossReason', 'lossNote', 'lossDate']);
            setEditData(prev => ({ ...prev, ...data }));
            setShowLossModal(false);
          }}
          onCancel={() => setShowLossModal(false)}
        />
      )}
    </div>
  );
}

/* ── Copy helper ── */
function CopyRow({ icon, value, href, mono }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  function doCopy(e) {
    e.stopPropagation();
    navigator.clipboard.writeText(value).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5px 0', borderBottom: '1px solid var(--border)' }}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2" style={{ flexShrink: 0 }}>{icon}</svg>
      <span style={{ flex: 1, fontSize: 12, color: 'var(--text)', fontFamily: mono ? 'var(--mono)' : 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {href ? (
          <a href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer"
            style={{ color: 'var(--accent)', textDecoration: 'none' }} onClick={e => e.stopPropagation()}>{value}</a>
        ) : value}
      </span>
      <button onClick={doCopy} title="Kopieren"
        style={{ flexShrink: 0, width: 24, height: 24, borderRadius: 5, background: copied ? 'rgba(22,163,74,0.1)' : 'var(--bg4)', border: `1px solid ${copied ? 'rgba(22,163,74,0.3)' : 'var(--border)'}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .15s' }}>
        {copied
          ? <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg>
          : <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
        }
      </button>
    </div>
  );
}

/* ── Bestandskunden intelligence section ── */
function BestandskundenSection({ branch, bestandskunden }) {
  const matches = branch
    ? bestandskunden.filter(k => (k.branch || '').toLowerCase().trim() === branch.toLowerCase().trim()).slice(0, 5)
    : [];

  const [open, setOpen] = useState(true);
  const [popupId, setPopupId] = useState(null);
  const popupRef = useRef(null);

  useEffect(() => {
    if (!popupId) return;
    function handleClick(e) {
      if (popupRef.current && !popupRef.current.contains(e.target)) setPopupId(null);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [popupId]);

  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <button onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', flex: 1 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2" style={{ transform: open ? 'rotate(0)' : 'rotate(-90deg)', transition: 'transform .2s' }}><polyline points="6 9 12 15 18 9"/></svg>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
            Ähnliche Bestandskunden
            {matches.length > 0 && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 600, color: '#16a34a', background: 'rgba(22,163,74,0.1)', border: '1px solid rgba(22,163,74,0.2)', borderRadius: 10, padding: '1px 7px' }}>{matches.length}</span>}
          </span>
        </button>
      </div>
      {open && (
        <div style={{ padding: '0 16px 14px' }}>
          {!branch ? (
            <div style={{ padding: '10px 12px', borderRadius: 9, background: 'var(--bg3)', border: '1px solid var(--border)', fontSize: 12, color: 'var(--text3)', textAlign: 'center', lineHeight: 1.5 }}>
              Branche beim Lead hinterlegen<br/>um Referenzen zu sehen
            </div>
          ) : matches.length === 0 ? (
            <div style={{ padding: '10px 12px', borderRadius: 9, background: 'var(--bg3)', border: '1px solid var(--border)', fontSize: 12, color: 'var(--text3)', textAlign: 'center', lineHeight: 1.5 }}>
              Keine Bestandskunden in<br/>
              <strong style={{ color: 'var(--text2)' }}>{branch}</strong> gefunden
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#16a34a', background: 'rgba(22,163,74,0.08)', border: '1px solid rgba(22,163,74,0.2)', borderRadius: 20, padding: '2px 9px' }}>{branch}</span>
                <span style={{ fontSize: 11, color: 'var(--text3)' }}>— Branchenreferenzen</span>
              </div>
              {matches.map((k, i) => {
                const domainHref = k.domain ? (k.domain.startsWith('http') ? k.domain : 'https://' + k.domain) : null;
                const domainDisplay = k.domain ? k.domain.replace(/^https?:\/\/(www\.)?/, '') : null;
                const address = [k.street, [k.zip, k.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
                const isOpen = popupId === (k.id || i);
                return (
                  <div key={k.id || i} style={{ position: 'relative' }}>
                    {/* Compact card — entire card is clickable */}
                    <div onClick={e => { e.stopPropagation(); setPopupId(isOpen ? null : (k.id || i)); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 10px', borderRadius: 9, background: 'var(--bg3)', border: `1.5px solid ${isOpen ? 'rgba(22,163,74,0.45)' : 'rgba(22,163,74,0.18)'}`, cursor: 'pointer', transition: 'border-color .12s' }}
                      onMouseEnter={e => { if (!isOpen) e.currentTarget.style.borderColor = 'rgba(22,163,74,0.35)'; }}
                      onMouseLeave={e => { if (!isOpen) e.currentTarget.style.borderColor = 'rgba(22,163,74,0.18)'; }}>
                      <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'rgba(22,163,74,0.12)', color: '#16a34a', border: '2px solid rgba(22,163,74,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                        {k.name.split(' ').slice(0,2).map(x => x[0]).join('').toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{k.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {k.company || '–'}{k.status ? ` · ${k.status}` : ''}
                        </div>
                      </div>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={isOpen ? '#16a34a' : 'var(--text3)'} strokeWidth="2" style={{ flexShrink: 0, transition: 'transform .2s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}><polyline points="6 9 12 15 18 9"/></svg>
                    </div>

                    {/* Popup */}
                    {isOpen && (
                      <div ref={popupRef}
                        style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 120, background: 'var(--bg2)', border: '1.5px solid rgba(22,163,74,0.35)', borderRadius: 10, padding: '10px 12px', boxShadow: '0 8px 24px rgba(15,23,42,0.18)', animation: 'flyIn .12s ease' }}>
                        {/* Header: full name + company + status, never truncated */}
                        <div style={{ marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid var(--border)' }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{k.name}</div>
                          {k.company && <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 1 }}>{k.company}</div>}
                          {k.status && <div style={{ fontSize: 11, color: '#16a34a', marginTop: 2 }}>{k.status}</div>}
                        </div>
                        {/* Contact rows with copy buttons */}
                        <CopyRow icon={<path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>} value={k.tel} href={k.tel ? `tel:${k.tel}` : null} mono />
                        <CopyRow icon={<><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 7l10 7 10-7"/></>} value={k.email} href={k.email ? `mailto:${k.email}` : null} />
                        <CopyRow icon={<><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></>} value={domainDisplay} href={domainHref} mono />
                        <CopyRow icon={<><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></>} value={address} />
                        {k.note && (
                          <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text2)', fontStyle: 'italic', paddingLeft: 12, borderLeft: '2px solid var(--border)' }}>{k.note}</div>
                        )}
                        {!k.tel && !k.email && !k.domain && !address && !k.note && (
                          <div style={{ fontSize: 12, color: 'var(--text3)', textAlign: 'center', padding: '4px 0' }}>Keine Kontaktdaten hinterlegt</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'center', paddingTop: 2 }}>
                Nutze diese Referenzen im Gespräch für Vertrauensaufbau
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Left helpers ── */
function LeftSection({ title, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      <button onClick={() => setOpen(o => !o)} style={{ width: '100%', padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'none', border: 'none', cursor: 'pointer' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{title}</span>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2" style={{ transform: open ? 'rotate(0)' : 'rotate(-90deg)', transition: 'transform .2s' }}><polyline points="6 9 12 15 18 9"/></svg>
      </button>
      {open && <div style={{ padding: '0 20px 14px' }}>{children}</div>}
    </div>
  );
}

/* ── Right helpers ── */
function RightSection({ title, count, actionLabel, onAction, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <button onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', flex: 1 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2" style={{ transform: open ? 'rotate(0)' : 'rotate(-90deg)', transition: 'transform .2s' }}><polyline points="6 9 12 15 18 9"/></svg>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{title}{count != null ? ` (${count})` : ''}</span>
        </button>
        {actionLabel && (
          <button onClick={onAction} style={{ fontSize: 12, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, padding: '2px 4px', borderRadius: 4, transition: 'background .1s' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-bg)'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}>
            {actionLabel}
          </button>
        )}
      </div>
      {open && <div style={{ padding: '0 16px 14px' }}>{children}</div>}
    </div>
  );
}

function RightDetailRow({ label, value, link, valueColor }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 12, marginBottom: 6 }}>
      <span style={{ color: 'var(--text3)', flexShrink: 0, marginRight: 8 }}>{label}</span>
      <span style={{ color: link ? 'var(--accent)' : (valueColor || 'var(--text2)'), fontWeight: link ? 500 : 400, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 170 }}>{value || '–'}</span>
    </div>
  );
}
